import { createSlice } from '@reduxjs/toolkit'
import * as blackjack from 'engine-blackjack-ts'
import { generateUUID } from '../helpers/js-helpers';
import {
  State as IState
} from 'engine-blackjack-ts'
import { DefaultEnums, GamePlayState } from "../Enums/GameEnums";
import { setLocalStorage } from '../helpers/LocalStorage';
import { MODIFIER_NAME, PLAYER_CLASSES } from '../Enums/Attributes';
import { getSpriteByGenber as getSpriteByGender } from '../helpers/helpers';
import { LocationNames, ClassNames, SpriteGenderNames } from '../Enums/NameEnums';
import { dealDamageToPlayer, dealDamageToMonster, spawnNewMonster, generateNewLocation, resetState, isPlayerValue21, isPlayerValueBJ, isMonsterValueBJ } from './rpgLogic'
import { addLevelEndProgress, addBetProgress, addHealthPotionProgress, ProgressData } from "./achievementLogic";
export interface GameState {
  progressData: [ProgressData?];
  hasGameStarted: boolean;
  hasGameLoaded: boolean;
  hasBattleStarted: boolean;
  gamePlayState: GamePlayState;
  rewardIndex: number;
  userId: string;
  timePlayed: number;
  startTime: Date;
  BJGameCounter: number;
  level: number;
  levelName: LocationNames;
  levelFullName: string;
  stage: number;
  storyNumber: number;
  engineState: IState | null
  bet: number,
  balance: number,
  player: Character
  monster: {
    isVisible: boolean;
    name: string,
    attributes: Attributes,
    namePrefix: string
  }
  settings: {
    soundOn: boolean
  };
  popup: {
    isVisible: boolean,
    contentTop: string,
    contentBottom: string
  },
  isScoreboardVisible: boolean,
  isHelpVisible: boolean,
  isCreditsVisible: boolean,
  isMuted: boolean,
}

interface Character {
  name: string,
  className: ClassNames,
  attributes: Attributes,
  maxLevelReached: number,
  numberOfDeaths: number,
  monstersBeaten: number,
  gender: SpriteGenderNames,
  healthPotionCount: number,
  sprite: string
}

interface Attributes {
  att: number,
  def: number,
  pierce: number,
  maxHp: number,
  currentHp: number
}

const initialState: GameState = {
  progressData: [],
  hasGameStarted: false,
  engineState: null,
  storyNumber: 1,
  userId: null,
  BJGameCounter: 0,
  timePlayed: 0,
  startTime: null,
  hasGameLoaded: false,
  hasBattleStarted: false,
  gamePlayState: GamePlayState.GAME_START,
  rewardIndex: 0,
  level: 0,
  levelName: null,
  levelFullName: '',
  stage: 1,
  bet: DefaultEnums.DEFAULT_BET,
  balance: DefaultEnums.DEFAULT_BALANCE,
  player: null,
  monster: {
    isVisible: true,
    name: '',
    attributes: {
      att: 0,
      def: 0,
      pierce: 0,
      maxHp: 0,
      currentHp: 0
    },
    namePrefix: ''
  },
  settings: {
    soundOn: false
  },
  popup: {
    isVisible: false,
    contentTop: '',
    contentBottom: ''
  },
  isScoreboardVisible: false,
  isHelpVisible: false,
  isCreditsVisible: false,
  isMuted: false,
}

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    startGame: (state) => {
      resetState(state);
      state.userId = generateUUID();
      state.startTime = new Date();
      state.timePlayed = 0;
      state.gamePlayState = GamePlayState.STORY_INTRO
      state.hasGameStarted = true
      blackjackEngineWrapper.newInstance()
      state.engineState = blackjackEngineWrapper.getEngineState()
      setLocalStorage(state)
    },

    restartGame: (state) => {
      state.gamePlayState = GamePlayState.GAME_START
      blackjackEngineWrapper.newInstance()
      state.engineState = blackjackEngineWrapper.getEngineState()
      //setLocalStorage(state)
    },

    setGamePlayState: (state, action) => {
      state.gamePlayState = action.payload;
      setLocalStorage(state);
    },

    setStoryNumber: (state, action) => {
      state.storyNumber = action.payload;
      setLocalStorage(state);
    },

    pickNextLevel: (state) => {
      state.progressData = addLevelEndProgress(state);
      state.player.maxLevelReached++;
      generateNewLocation(state);
      spawnNewMonster(state);
      state.gamePlayState = GamePlayState.BETTING
      setLocalStorage(state)
    },

    changeAttributes: (state, action: { payload: { enumValue: MODIFIER_NAME, value: number } }) => {
      let currentAttributes = state.player.attributes;
      if (state.rewardIndex === 0) {
        state.balance = state.balance + DefaultEnums.GOLD_PER_CHEST;
        state.player.healthPotionCount = state.player.healthPotionCount + DefaultEnums.HEALTH_POTION_PER_CHEST;
        currentAttributes.att = currentAttributes.att + 1;
        currentAttributes.def = currentAttributes.def + 1;
        currentAttributes.pierce = currentAttributes.pierce + 1;
        currentAttributes.maxHp = currentAttributes.maxHp + 1;
        currentAttributes.currentHp = currentAttributes.currentHp + 1;
      }
      switch (action.payload.enumValue) {
        case MODIFIER_NAME.att: currentAttributes.att = currentAttributes.att + action.payload.value; break
        case MODIFIER_NAME.def: currentAttributes.def = currentAttributes.def + action.payload.value; break
        case MODIFIER_NAME.hp: currentAttributes.maxHp = currentAttributes.maxHp + action.payload.value; currentAttributes.currentHp = currentAttributes.currentHp + action.payload.value; break
        case MODIFIER_NAME.pierce: currentAttributes.pierce = currentAttributes.pierce + action.payload.value; break
      }
      state.rewardIndex++;
      state.player.attributes = Object.assign(state.player.attributes, { ...currentAttributes });
    },
    pickClass: (state, action: {
      payload: {
        className: ClassNames,
        name: string,
        gender: SpriteGenderNames
      }
    }) => {
      const profession = PLAYER_CLASSES.find(x => x.name === action.payload.className);
      const player: Character = {
        sprite: getSpriteByGender(profession, action.payload.gender),
        maxLevelReached: 0,
        numberOfDeaths: 0,
        monstersBeaten: 0,
        className: action.payload.className,
        name: action.payload.name === '' ? 'Nameless' : action.payload.name,
        healthPotionCount: profession.potion + DefaultEnums.DEFAULT_POTIONS,
        gender: action.payload.gender,

        attributes: {
          att: profession.att + DefaultEnums.DEFAULT_ATTACK,
          def: profession.def + DefaultEnums.DEFAULT_DEFENCE,
          pierce: profession.pierce + DefaultEnums.DEFAULT_PIERCE,
          maxHp: profession.hp + DefaultEnums.DEFAULT_HP,
          currentHp: profession.hp + DefaultEnums.DEFAULT_HP
        }
      }
      state.balance = DefaultEnums.DEFAULT_BALANCE;
      state.player = Object.assign({}, { ...player });
      state.gamePlayState = GamePlayState.STORY_OUTRO;
      generateNewLocation(state);
      spawnNewMonster(state);
      setLocalStorage(state);
    },

    resolve: (state) => {
      if (!isTie(state.engineState)) {
        if (state.engineState.wonOnRight > 0) {
          dealDamageToMonster(state);
        } else if (state.engineState.dealerHasBlackjack) {
          dealDamageToPlayer(state, true);
        } else {
          dealDamageToPlayer(state);
        }
      }

      blackjackEngineWrapper.newInstance();
      state.engineState = blackjackEngineWrapper.getEngineState()

      setLocalStorage(state);

    },

    continueGame: (state, action) => {
      const newState: GameState = Object.assign({}, { ...action.payload });
      const hasDied = newState.gamePlayState === GamePlayState.DEFEAT;

      if (
        newState.gamePlayState === GamePlayState.GAME_START ||
        newState.gamePlayState === GamePlayState.DEFEAT ||
        newState.gamePlayState === GamePlayState.PLAYER_ACTION
      ) {
        newState.gamePlayState = GamePlayState.BETTING;
      }

      if (!newState.userId) {
        newState.userId = generateUUID();
      }

      if (!newState.startTime) {
        newState.startTime = new Date();
      }

      if (!newState.progressData) {
        newState.progressData = [];
      }

      newState.popup.isVisible = false;
      newState.popup.contentTop = '';
      newState.popup.contentBottom = '';

      const existingBJGame = newState.engineState && !hasDied ? newState.engineState : null;

      if (existingBJGame && existingBJGame.stage !== 'ready') {
        newState.gamePlayState = GamePlayState.PLAYER_ACTION;
      }

      blackjackEngineWrapper.newInstance(existingBJGame);
      newState.engineState = blackjackEngineWrapper.getEngineState();
      return newState;
    },

    loadGame: (state) => {
      state.hasGameLoaded = true;
    },

    deal: (state, { payload }) => {
      state.monster.isVisible = true;
      if (blackjackEngineWrapper.instance) {
        if (!state.isMuted) {
          const audio = new Audio('BlackJack RPG_Coin SFX_20.10.2023.mp3');
          audio.play();
        }
        state.BJGameCounter++;
        state.balance = state.balance - state.bet
        state.progressData = addBetProgress(state);
        blackjackEngineWrapper.instance.dispatch(blackjack.actions.deal({ bet: payload }))
        state.engineState = blackjackEngineWrapper.getEngineState()

        state.gamePlayState = GamePlayState.PLAYER_CARD_REVEAL
        setLocalStorage(state);
      }
      else {
        console.warn('no bj instance found')
      }
    },

    processDeal: (state) => {
      const BJ = isPlayerValueBJ(state);
      const sideBetInfo = state.engineState.sideBetsInfo;

      if (sideBetInfo.luckyLucky !== 0 || sideBetInfo.perfectPairs !== 0) {
        state.player.healthPotionCount = state.player.healthPotionCount + DefaultEnums.HEALTH_POTION_PER_SIDEWIN;
      }

      if (BJ) {
        state.player.healthPotionCount = state.player.healthPotionCount + DefaultEnums.HEALTH_POTION_PER_BJ;
        dealDamageToMonster(state, true);
        initialiseNewBJ(state);
      } else if (isMonsterValueBJ(state)) {
        dealDamageToPlayer(state, true);
        initialiseNewBJ(state);
      } else {
        state.gamePlayState = GamePlayState.PLAYER_ACTION
      }
      setLocalStorage(state)
    },

    hit: (state) => {
      if (blackjackEngineWrapper.instance) {
        blackjackEngineWrapper.instance.dispatch(blackjack.actions.hit({ position: 'right' }))
        state.engineState = blackjackEngineWrapper.getEngineState()
      }
    },

    processHit: (state) => {
      if (state.engineState.stage === 'done' && state.engineState.handInfo.right.playerHasBusted) {
        dealDamageToPlayer(state);
        initialiseNewBJ(state);
      } else if (isPlayerValue21(state)) {
        dealDamageToMonster(state);
        initialiseNewBJ(state);
      }

      if (state.gamePlayState !== GamePlayState.DEFEAT) {
        setLocalStorage(state);
      }

    },

    increaseBet: (state, { payload }) => {
      const balance = state.balance
      const result = state.bet + payload
      state.bet = result >= balance ? Math.max(balance, 1) : result
      setLocalStorage(state);
    },

    reduceBet: (state, { payload }) => {
      const result = state.bet - payload
      state.bet = result < 1 ? 1 : result
      setLocalStorage(state);
    },

    stand: (state) => {
      if (blackjackEngineWrapper.instance) {
        blackjackEngineWrapper.instance.dispatch(blackjack.actions.stand({ position: 'right' }))
        state.engineState = blackjackEngineWrapper.getEngineState()

        state.gamePlayState = GamePlayState.DEALER_CARD_REVEAL
        setLocalStorage(state);
      }
    },

    drinkHealingPotion: (state) => {
      if (!state.isMuted) {
        const audio = new Audio('BlackJack RPG_PowerUp1 SFX_20.10.2023.mp3');
        audio.play();
      }
      const currentHp = state.player.attributes.currentHp
      const maxHp = state.player.attributes.maxHp
      const percent20 = Math.round(maxHp * (DefaultEnums.HEAL_AMOUNT / 100))
      const result = currentHp + percent20
      const roundedResult = Math.floor(result)
      state.player.attributes.currentHp = roundedResult > maxHp ? maxHp : roundedResult
      state.player.healthPotionCount--;
      state.progressData = addHealthPotionProgress(state);
      setLocalStorage(state);
    },

    closePopup: (state) => {
      state.popup.isVisible = false
      state.popup.contentTop = ''
      state.popup.contentBottom = '';
      state.monster.isVisible = true;
    },

    showScoreboard: (state) => {
      state.isScoreboardVisible = true
    },

    hideScoreboard: (state) => {
      state.isScoreboardVisible = false
    },

    showHelp: (state) => {
      state.isHelpVisible = true
    },

    showCredits: (state) => {
      state.isCreditsVisible = true
    },

    hideCredits: (state) => {
      state.isCreditsVisible = false
    },

    setMute: (state, { payload }) => {
      state.isMuted = payload;
      setLocalStorage(state);
    },

    hideHelp: (state) => {
      state.isHelpVisible = false
    },
  },
})

function isTie(engineState) {
  if (Math.max(engineState.handInfo?.right?.playerValue?.hi, engineState.handInfo?.right?.playerValue?.low) === Math.max(engineState?.dealerValue?.hi, engineState?.dealerValue?.low)) {
    console.log("It's a tie")
    return true
  }
  return false
}

export function initialiseNewBJ(state) {
  blackjackEngineWrapper.newInstance();
  state.engineState = blackjackEngineWrapper.getEngineState();
}

export const blackjackEngineWrapper = {
  instance: null,
  newInstance: function (existingBJGame = null) {
    this.instance = new blackjack.Game(existingBJGame, {
      decks: 1,
      standOnSoft17: true,
      double: 'any',
      doubleAfterSplit: true,
      split: false,
      surrender: false,
      insurance: false,
      showdownAfterAceSplit: false
    })
  },
  getEngineState: function () {
    const engineState = { ...this.instance.getState() }
    // return Object.assign({},{...engineState})
    return {
      ...engineState,
      deck: [...engineState.deck],
    }
  }
}

export const gameActions = gameSlice.actions;
export default gameSlice.reducer;
